'use client';

import { config } from './config';
import { getViewerGeo, type ViewerGeo } from './geo';

// localStorage keys
const KEYS = {
    VISITOR_ID: 'vv_visitor_id',
    SESSION_ID: 'vv_session_id',
    FIRST_TOUCH: 'vv_first_touch',
    LAST_TOUCH: 'vv_last_touch',
    ATTRIBUTION: 'vv_attribution',
};

const COOKIE_NAME = 'vv_attr';
const COOKIE_DOMAIN = '.vibevite.com';
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
const CUSTOM_PARAMS = ['track_code', 'long_code', 'ref_type', 'ref_event_id', 'ref_host_id',
    'ref_guest_id', 'referral_code', 'gift_code'];
const CLICK_IDS = ['gclid', 'fbclid', 'msclkid', 'ttclid'];
const ALL_PARAMS = [...UTM_PARAMS, ...CUSTOM_PARAMS, ...CLICK_IDS];

// Params to strip from URL after capture
const STRIP_PARAMS = [...ALL_PARAMS, 'vv_vid', 'vv_sid'];

interface AttributionData {
    visitor_id: string;
    session_id: string;
    landing_page_url: string;
    landing_page_path: string;
    landing_page_type?: string;
    landing_page_name?: string;
    landing_page_slug?: string;
    landing_page_category?: string;
    referrer_url: string;
    referrer_domain: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    utm_id?: string;
    track_code?: string;
    long_code?: string;
    ref_type?: string;
    ref_event_id?: string;
    ref_host_id?: string;
    ref_guest_id?: string;
    referral_code?: string;
    gift_code?: string;
    gclid?: string;
    fbclid?: string;
    msclkid?: string;
    ttclid?: string;
    device_type?: string;
    browser?: string;
    os?: string;
    captured_at: string;
}

function generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    // Fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function getOrCreateVisitorId(): string {
    let vid = localStorage.getItem(KEYS.VISITOR_ID);
    if (!vid) {
        vid = `vid-${generateUUID()}`;
        localStorage.setItem(KEYS.VISITOR_ID, vid);
    }
    return vid;
}

function getOrCreateSessionId(): string {
    let sid = sessionStorage.getItem(KEYS.SESSION_ID);
    if (!sid) {
        sid = `sid-${generateUUID()}`;
        sessionStorage.setItem(KEYS.SESSION_ID, sid);
    }
    return sid;
}

function detectDeviceType(): string {
    const ua = navigator.userAgent;
    if (/tablet|ipad/i.test(ua)) return 'tablet';
    if (/mobile|iphone|android.*mobile/i.test(ua)) return 'mobile';
    return 'desktop';
}

function detectBrowser(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    return 'Other';
}

function detectOS(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Win')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (/iPhone|iPad/.test(ua)) return 'iOS';
    if (ua.includes('Android')) return 'Android';
    return 'Other';
}

// --- Landing page context parser ---

const OCCASION_SLUGS = [
    'anniversary-video', 'baby-shower-video', 'birthday-video', 'engagement-video',
    'farewell-video', 'fathers-day-video', 'gender-reveal-video', 'get-well-video',
    'graduation-video', 'holiday-video', 'memorial-video', 'mothers-day-video',
    'promotion-video', 'retirement-video', 'thank-you-video', 'valentines-day-video',
    'wedding-video', 'employee-birthday-video', 'work-anniversary-video',
    'team-achievement-video', 'company-milestone-video', 'live-streaming',
    'anniversary', 'baby-shower', 'birthday', 'engagement', 'farewell', 'fathers-day',
    'gender-reveal', 'get-well', 'graduation', 'holiday', 'memorial', 'mothers-day',
    'promotion', 'retirement', 'thank-you', 'valentines-day', 'wedding',
    'employee-birthday', 'work-anniversary', 'team-achievement', 'company-milestone',
];

function toTitleCase(s: string): string {
    return s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseLandingPageContext(pathname: string): {
    landing_page_type: string;
    landing_page_name: string;
    landing_page_slug: string;
    landing_page_category?: string;
} {
    const parts = pathname.replace(/^\//, '').split('/').filter(Boolean);
    const slug = parts[0] || '';

    if (!slug) {
        return { landing_page_type: 'home', landing_page_name: 'Home', landing_page_slug: '' };
    }
    if (slug === 'business') {
        if (parts.length === 1) {
            return { landing_page_type: 'business_hub', landing_page_name: 'Business Overview', landing_page_slug: 'business', landing_page_category: 'business' };
        }
        if (parts[1] === 'vibewall') {
            if (parts.length === 2) {
                return { landing_page_type: 'business_vibewall', landing_page_name: 'VibeWall Hub', landing_page_slug: 'business-vibewall', landing_page_category: 'business_vibewall' };
            }
            return { landing_page_type: 'business_feature', landing_page_name: toTitleCase(parts[2] || ''), landing_page_slug: parts[2] || '', landing_page_category: 'business_vibewall' };
        }
        if (parts[1] === 'features') {
            return { landing_page_type: 'business_feature', landing_page_name: toTitleCase(parts[2] || ''), landing_page_slug: parts[2] || '', landing_page_category: 'business_feature' };
        }
        if (parts[1] === 'occasions') {
            return { landing_page_type: 'business_occasion', landing_page_name: toTitleCase(parts[2] || ''), landing_page_slug: parts[2] || '', landing_page_category: (parts[2] || '').replace(/-/g, '_') };
        }
        return { landing_page_type: 'business_page', landing_page_name: toTitleCase(parts[1] || 'Business'), landing_page_slug: parts[1] || 'business', landing_page_category: 'business' };
    }
    if (slug === 'personal') {
        if (parts.length === 1) {
            return { landing_page_type: 'personal_hub', landing_page_name: 'Personal Overview', landing_page_slug: 'personal', landing_page_category: 'personal' };
        }
    }
    if (slug === 'pricing') {
        return { landing_page_type: 'pricing', landing_page_name: 'Pricing', landing_page_slug: 'pricing', landing_page_category: 'marketing' };
    }
    if (slug === 'blog') {
        if (parts.length === 1) {
            return { landing_page_type: 'blog_list', landing_page_name: 'Blog', landing_page_slug: 'blog' };
        }
        return { landing_page_type: 'blog_post', landing_page_name: toTitleCase(parts[1] || ''), landing_page_slug: parts[1] || '', landing_page_category: 'blog' };
    }
    if (OCCASION_SLUGS.includes(slug)) {
        const category = slug.endsWith('-video') ? slug.replace(/-video$/, '') : slug;
        const isBusinessOccasion = slug.startsWith('employee-') || slug.startsWith('work-') || slug.startsWith('team-') || slug.startsWith('company-');
        return {
            landing_page_type: isBusinessOccasion ? 'business_occasion' : 'occasion',
            landing_page_name: toTitleCase(slug),
            landing_page_slug: slug,
            landing_page_category: category.replace(/-/g, '_'),
        };
    }
    return { landing_page_type: 'page', landing_page_name: toTitleCase(slug), landing_page_slug: slug };
}

function isExternalReferrer(domain: string): boolean {
    if (!domain) return false;
    const internal = ['vibevite.com', 'account.vibevite.com', 'www.vibevite.com'];
    return !internal.some((d) => domain === d || domain.endsWith('.' + d));
}

function hasMeaningfulSource(data: Record<string, string | undefined>): boolean {
    if (data.utm_source || data.track_code || data.ref_type) return true;
    if (data.gclid || data.fbclid || data.msclkid || data.ttclid) return true;
    if (data.referrer_domain && isExternalReferrer(data.referrer_domain)) return true;
    return false;
}

function setSharedCookie(visitorId: string, attribution: Record<string, unknown>): void {
    const value = JSON.stringify({ vid: visitorId, ...attribution });
    const encoded = encodeURIComponent(value);
    const isLocalhost = window.location.hostname === 'localhost';
    const domain = isLocalhost ? '' : `; domain=${COOKIE_DOMAIN}`;
    const secure = isLocalhost ? '' : '; secure';
    document.cookie = `${COOKIE_NAME}=${encoded}; max-age=${COOKIE_MAX_AGE}; path=/${domain}; samesite=lax${secure}`;
}

function cleanUrl(): void {
    const url = new URL(window.location.href);
    let changed = false;
    for (const param of STRIP_PARAMS) {
        if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            changed = true;
        }
    }
    if (changed) {
        window.history.replaceState({}, '', url.toString());
    }
}

async function fireSessionBeacon(data: AttributionData): Promise<void> {
    const endpoint = `${config.API_BASE_URL}/tracking/session`;
    let sessionSaved = false;

    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ schema_version: '1.1', environment: process.env.NODE_ENV || 'production', attribution_model: 'last_non_direct_click', ...data }),
            keepalive: true,
        });
        sessionSaved = response.ok;
        if (response.ok && (await response.json()).cloudfront_geo_complete === true) return;
    } catch { /* fall back to the site geo endpoint */ }

    const geo = await getViewerGeo();
    if (geo.country || !sessionSaved) sendSessionBeacon(data, geo);
}

function sendSessionBeacon(data: AttributionData, geo: ViewerGeo): void {
    const endpoint = `${config.API_BASE_URL}/tracking/session`;
    const payload = {
        schema_version: '1.1',
        environment: process.env.NODE_ENV || 'production',
        attribution_model: 'last_non_direct_click',
        ...data,
        ...geo,
    };
    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(endpoint, blob)) return;
    }
    fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
    }).catch(() => { /* non-blocking */ });
}

export function captureAttribution(): void {
    if (typeof window === 'undefined') return;

    const visitorId = getOrCreateVisitorId();
    const sessionId = getOrCreateSessionId();

    const params = new URLSearchParams(window.location.search);
    const captured: Record<string, string | undefined> = {};

    for (const key of ALL_PARAMS) {
        const val = params.get(key);
        if (val) captured[key] = val.trim();
    }

    // Parse referrer
    let referrerUrl = '';
    let referrerDomain = '';
    try {
        if (document.referrer) {
            referrerUrl = document.referrer;
            referrerDomain = new URL(document.referrer).hostname;
        }
    } catch { /* invalid referrer URL */ }

    const pageContext = parseLandingPageContext(window.location.pathname);

    const attributionData: AttributionData = {
        visitor_id: visitorId,
        session_id: sessionId,
        landing_page_url: window.location.href,
        landing_page_path: window.location.pathname,
        landing_page_type: pageContext.landing_page_type,
        landing_page_name: pageContext.landing_page_name,
        landing_page_slug: pageContext.landing_page_slug,
        landing_page_category: pageContext.landing_page_category,
        referrer_url: referrerUrl,
        referrer_domain: referrerDomain,
        ...captured,
        device_type: detectDeviceType(),
        browser: detectBrowser(),
        os: detectOS(),
        captured_at: new Date().toISOString(),
    };

    // First-touch: only set if not already stored
    if (!localStorage.getItem(KEYS.FIRST_TOUCH)) {
        localStorage.setItem(KEYS.FIRST_TOUCH, JSON.stringify(attributionData));
    }

    // Last-touch: only update if meaningful source
    if (hasMeaningfulSource(captured)) {
        localStorage.setItem(KEYS.LAST_TOUCH, JSON.stringify(attributionData));
    }

    // Always save current session attribution
    localStorage.setItem(KEYS.ATTRIBUTION, JSON.stringify(attributionData));

    // Set shared cookie for cross-domain
    setSharedCookie(visitorId, {
        sid: sessionId,
        utm_source: captured.utm_source,
        utm_medium: captured.utm_medium,
        utm_campaign: captured.utm_campaign,
        track_code: captured.track_code,
        ref_type: captured.ref_type,
    });

    // Clean tracking params from URL
    cleanUrl();

    // Fire session beacon to backend
    fireSessionBeacon(attributionData);
}

export function getAttributionData(): AttributionData | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(KEYS.ATTRIBUTION);
    return raw ? JSON.parse(raw) : null;
}

export function getFirstTouch(): AttributionData | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(KEYS.FIRST_TOUCH);
    return raw ? JSON.parse(raw) : null;
}

export function getLastTouch(): AttributionData | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(KEYS.LAST_TOUCH);
    return raw ? JSON.parse(raw) : null;
}

export function getVisitorId(): string {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(KEYS.VISITOR_ID) || '';
}

export function getSessionId(): string {
    if (typeof window === 'undefined') return '';
    return sessionStorage.getItem(KEYS.SESSION_ID) || '';
}

export function buildCTAUrl(baseUrl: string): string {
    if (typeof window === 'undefined') return baseUrl;

    const url = new URL(baseUrl);
    const attribution = getAttributionData();

    // Forward visitor and session IDs
    const vid = getVisitorId();
    const sid = getSessionId();
    if (vid) url.searchParams.set('vv_vid', vid);
    if (sid) url.searchParams.set('vv_sid', sid);

    // Forward UTM params
    if (attribution) {
        for (const key of UTM_PARAMS) {
            const val = attribution[key as keyof AttributionData];
            if (val && typeof val === 'string') {
                url.searchParams.set(key, val);
            }
        }
        // Forward custom tracking params
        if (attribution.track_code) url.searchParams.set('track_code', attribution.track_code);
        if (attribution.ref_type) url.searchParams.set('ref_type', attribution.ref_type);
        if (attribution.ref_event_id) url.searchParams.set('ref_event_id', attribution.ref_event_id);
        if (attribution.ref_host_id) url.searchParams.set('ref_host_id', attribution.ref_host_id);
        if (attribution.referral_code) url.searchParams.set('referral_code', attribution.referral_code);
    }

    return url.toString();
}
